import { FaMapMarkerAlt, FaRoad, FaCarCrash, FaThumbsUp, FaArrowDown } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Hero from "./Hero";
import Contact from "./contact/Contact";
import useInView from "../hooks/useInView";

const Home = () => {
  const navigate = useNavigate();
  const token = useSelector((state) => state.auth.token);
  const featuresReveal = useInView();
  const stepsReveal = useInView();
  const ctaReveal = useInView();

  const handleReportClick = () => {
    navigate(token ? "/report" : "/login");
  };

  const features = [
    {
      icon: FaRoad,
      title: "Easy reporting",
      description: "Pin a hazard, add a photo, and submit in minutes.",
      accent: "text-navy-800 bg-navy-900/5",
    },
    {
      icon: FaCarCrash,
      title: "Prevent accidents",
      description: "Surface dangerous spots before they cause damage.",
      accent: "text-amber-600 bg-amber-500/15",
    },
    {
      icon: FaThumbsUp,
      title: "Community impact",
      description: "Vote and comment so priorities stay visible.",
      accent: "text-emerald-700 bg-emerald-500/15",
    },
  ];

  const steps = [
    { step: "01", title: "Spot it", text: "See a pothole on your street." },
    { step: "02", title: "Report it", text: "Add location, photo, and severity." },
    { step: "03", title: "Rally it", text: "Neighbors vote and keep pressure on." },
    { step: "04", title: "Track it", text: "Follow status until it’s fixed." },
  ];

  return (
    <div className="min-h-screen overflow-x-hidden">
      <section className="hero-map-cursor relative overflow-hidden bg-gradient-to-br from-navy-950 via-navy-900 to-navy-700 text-white">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -left-24 top-8 h-72 w-72 rounded-full bg-amber-400/20 blur-3xl animate-soft-pulse" />
          <div className="absolute bottom-0 right-0 h-96 w-96 rounded-full bg-white/10 blur-3xl animate-float-slow" />
          <div className="absolute inset-0 opacity-[0.07] [background-image:linear-gradient(rgba(255,255,255,.4)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.4)_1px,transparent_1px)] [background-size:42px_42px] animate-grid-drift" />

          <span className="absolute left-[12%] top-[28%] hidden text-amber-400/80 animate-float sm:block">
            <FaMapMarkerAlt className="text-3xl drop-shadow-lg" />
          </span>
          <span className="absolute right-[16%] top-[34%] hidden text-white/40 animate-float-delayed md:block">
            <FaMapMarkerAlt className="text-2xl" />
          </span>
          <span className="absolute bottom-[22%] left-[22%] hidden text-amber-300/50 animate-float-slow lg:block">
            <FaMapMarkerAlt className="text-xl" />
          </span>
        </div>

        <div className="relative mx-auto flex min-h-[72vh] max-w-6xl flex-col items-center justify-center px-4 py-16 text-center sm:min-h-[82vh] sm:py-20">
          <div className="animate-rise mb-5 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-amber-300 backdrop-blur">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-soft-pulse" />
            Community road watch
          </div>

          <p className="animate-rise font-display text-4xl font-bold tracking-tight sm:text-6xl md:text-7xl">
            Fix<span className="text-amber-400">My</span>Road
          </p>

          <h1 className="animate-rise-delay mt-4 max-w-3xl font-display text-2xl font-semibold leading-tight sm:mt-5 sm:text-4xl md:text-5xl">
            Report potholes. Rally your street.{" "}
            <span className="text-amber-300">Get roads fixed.</span>
          </h1>

          <p className="animate-rise-delay-2 mt-4 max-w-xl text-base text-white/78 sm:mt-5 sm:text-lg">
            A community hub for spotting hazards, tracking repairs, and pushing
            for safer journeys.
          </p>

          <div className="animate-rise-delay-3 mt-8 flex w-full max-w-md flex-col gap-3 sm:mt-10 sm:max-w-none sm:flex-row sm:flex-wrap sm:items-center sm:justify-center">
            <button
              onClick={handleReportClick}
              className="group relative overflow-hidden rounded-xl bg-amber-500 px-7 py-3.5 font-semibold text-navy-950 transition hover:-translate-y-0.5 hover:bg-amber-400"
            >
              <span className="relative z-10">
                {token ? "Report a pothole" : "Get started"}
              </span>
              <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent transition duration-700 group-hover:translate-x-full" />
            </button>
            <button
              onClick={() => navigate("/potholes")}
              className="rounded-xl border border-white/25 bg-white/5 px-7 py-3.5 font-semibold text-white backdrop-blur transition hover:-translate-y-0.5 hover:bg-white/10"
            >
              Browse reports
            </button>
          </div>

          <button
            type="button"
            onClick={() =>
              document.getElementById("why-report")?.scrollIntoView({
                behavior: "smooth",
              })
            }
            className="mt-12 inline-flex flex-col items-center gap-2 text-sm text-white/55 transition hover:text-amber-300"
          >
            Explore
            <FaArrowDown className="animate-bounce-soft text-amber-400" />
          </button>
        </div>
      </section>

      <section id="why-report" className="mx-auto max-w-6xl px-4 py-14 sm:py-20">
        <div
          ref={featuresReveal.ref}
          className={`mx-auto max-w-2xl text-center transition duration-700 ${
            featuresReveal.inView
              ? "translate-y-0 opacity-100"
              : "translate-y-6 opacity-0"
          }`}
        >
          <p className="mb-2 text-sm font-semibold uppercase tracking-[0.18em] text-amber-600">
            Why it matters
          </p>
          <h2 className="font-display text-3xl font-bold text-navy-900 sm:text-4xl">
            Why report potholes?
          </h2>
          <p className="mt-3 text-navy-800/70">
            Small reports add up — visibility helps authorities prioritize the
            worst spots first.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div
                key={feature.title}
                className={`rounded-2xl border border-navy-900/8 bg-white/90 p-6 text-center shadow-[0_12px_40px_rgba(11,31,68,0.05)] transition duration-700 hover:-translate-y-1.5 hover:shadow-[0_20px_45px_rgba(11,31,68,0.12)] ${
                  featuresReveal.inView
                    ? "translate-y-0 opacity-100"
                    : "translate-y-8 opacity-0"
                }`}
                style={{ transitionDelay: `${index * 120}ms` }}
              >
                <div
                  className={`mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl ${feature.accent}`}
                >
                  <Icon className="text-2xl" />
                </div>
                <h3 className="mb-2 font-display text-xl font-semibold text-navy-900">
                  {feature.title}
                </h3>
                <p className="text-navy-800/70">{feature.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="bg-navy-900/[0.04] px-4 py-14 sm:py-18">
        <div
          ref={stepsReveal.ref}
          className="mx-auto max-w-6xl"
        >
          <div
            className={`mb-8 max-w-2xl transition duration-700 sm:mb-10 ${
              stepsReveal.inView
                ? "translate-y-0 opacity-100"
                : "translate-y-6 opacity-0"
            }`}
          >
            <p className="mb-2 text-sm font-semibold uppercase tracking-[0.18em] text-amber-600">
              Simple flow
            </p>
            <h2 className="font-display text-3xl font-bold text-navy-900 sm:text-4xl">
              From sidewalk to status update
            </h2>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {steps.map((item, index) => (
              <div
                key={item.step}
                className={`relative overflow-hidden rounded-2xl border border-navy-900/10 bg-white p-5 shadow-sm transition duration-700 hover:-translate-y-1 ${
                  stepsReveal.inView
                    ? "translate-y-0 opacity-100"
                    : "translate-y-8 opacity-0"
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <span className="font-display text-4xl font-bold text-amber-500/25">
                  {item.step}
                </span>
                <h3 className="mt-2 font-display text-lg font-semibold text-navy-900">
                  {item.title}
                </h3>
                <p className="mt-1 text-sm text-navy-800/70">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Hero />

      <section className="px-4 pb-4 pt-4 sm:pb-8">
        <div
          ref={ctaReveal.ref}
          className={`relative mx-auto max-w-6xl overflow-hidden rounded-3xl bg-gradient-to-br from-navy-950 via-navy-900 to-navy-700 px-6 py-12 text-white transition duration-700 sm:px-10 sm:py-14 ${
            ctaReveal.inView
              ? "translate-y-0 opacity-100"
              : "translate-y-8 opacity-0"
          }`}
        >
          <div className="pointer-events-none absolute -right-10 top-0 h-40 w-40 rounded-full bg-amber-400/20 blur-3xl" />
          <div className="relative flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
            <div className="max-w-xl">
              <h2 className="font-display text-3xl font-bold sm:text-4xl">
                See a hazard? Make it visible.
              </h2>
              <p className="mt-3 text-white/75">
                One clear report can spare dozens of vehicles — and push your
                street higher on the repair list.
              </p>
            </div>
            <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
              <button
                type="button"
                onClick={handleReportClick}
                className="rounded-xl bg-amber-500 px-6 py-3 font-semibold text-navy-950 transition hover:bg-amber-400"
              >
                {token ? "Report now" : "Join & report"}
              </button>
              <button
                type="button"
                onClick={() => navigate("/about")}
                className="rounded-xl border border-white/20 bg-white/5 px-6 py-3 font-semibold text-white transition hover:bg-white/10"
              >
                Learn more
              </button>
            </div>
          </div>
        </div>
      </section>

      <Contact embedded />
    </div>
  );
};

export default Home;
